import { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
    PnlPoint,
    BalancePoint,
    FlowPoint,
    ChartMode,
    FlowMode,
} from '../../api/useTraderProfile';

const MARGIN = { top: 8, right: 12, bottom: 24, left: 66 };
const TOOLTIP_W = 156;

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmtDollar(v: number): string {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
    return `${sign}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtAxis(v: number): string {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
    return `${sign}$${abs.toFixed(0)}`;
}

function fmtPnlSigned(v: number): string {
    return (v >= 0 ? '+' : '') + fmtDollar(v);
}

// ---------------------------------------------------------------------------
// Flow bar config
// ---------------------------------------------------------------------------

function flowBarKeys(mode: FlowMode): {
    posKey: keyof FlowPoint;
    negKey: keyof FlowPoint;
} {
    switch (mode) {
        case 'deposit_withdraw':
            return { posKey: 'deposit_usd', negKey: 'withdraw_usd' };
        case 'buy_sell':
            return { posKey: 'buy_volume_usd', negKey: 'sell_volume_usd' };
        case 'inflow_outflow':
            return { posKey: 'inflow_usd', negKey: 'outflow_usd' };
    }
}

// ---------------------------------------------------------------------------
// Aggregate hourly FlowPoints into daily buckets (UTC day boundary)
// Returns one entry per day that had any data, with all 6 fields summed.
// ---------------------------------------------------------------------------

interface DailyFlow {
    date: Date; // midnight UTC of that day
    deposit_usd: number;
    withdraw_usd: number;
    buy_volume_usd: number;
    sell_volume_usd: number;
    inflow_usd: number;
    outflow_usd: number;
}

function aggregateFlowsToDaily(flowPoints: FlowPoint[]): DailyFlow[] {
    const buckets = new Map<number, DailyFlow>();

    for (const fp of flowPoints) {
        const d = new Date(fp.ts);
        // truncate to UTC midnight
        const midnight = Date.UTC(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate()
        );
        const existing = buckets.get(midnight);
        if (existing) {
            existing.deposit_usd += fp.deposit_usd;
            existing.withdraw_usd += fp.withdraw_usd;
            existing.buy_volume_usd += fp.buy_volume_usd;
            existing.sell_volume_usd += fp.sell_volume_usd;
            existing.inflow_usd += fp.inflow_usd;
            existing.outflow_usd += fp.outflow_usd;
        } else {
            buckets.set(midnight, {
                date: new Date(midnight),
                deposit_usd: fp.deposit_usd,
                withdraw_usd: fp.withdraw_usd,
                buy_volume_usd: fp.buy_volume_usd,
                sell_volume_usd: fp.sell_volume_usd,
                inflow_usd: fp.inflow_usd,
                outflow_usd: fp.outflow_usd,
            });
        }
    }

    return Array.from(buckets.values()).sort(
        (a, b) => a.date.getTime() - b.date.getTime()
    );
}

const ONE_DAY_MS = 86_400_000;
const SMOOTH_STEPS = 12;

function smoothstep(t: number): number {
    return t * t * (3 - 2 * t);
}

interface SeriesPoint {
    date: Date;
    value: number;
}

function toDailyRamps(
    points: SeriesPoint[],
    activityWeight: (dayStartMs: number) => number
): SeriesPoint[] {
    if (points.length < 2) return points;

    const out: SeriesPoint[] = [points[0]];
    for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const cur = points[i];
        const gapDays: number[] = [];
        for (let t = +prev.date; t < +cur.date; t += ONE_DAY_MS) {
            gapDays.push(t);
        }
        if (gapDays.length <= 1 || cur.value === prev.value) {
            const rampStart = Math.max(+prev.date, +cur.date - ONE_DAY_MS);
            if (rampStart > +prev.date) {
                out.push({ date: new Date(rampStart), value: prev.value });
            }
            out.push(cur);
            continue;
        }
        const weights = gapDays.map(t => {
            const w = activityWeight(t);
            return Number.isFinite(w) ? Math.max(0, w) : 0;
        });
        const total = weights.reduce((a, b) => a + b, 0);
        if (total <= 0) {
            const rampStart = Math.max(+prev.date, +cur.date - ONE_DAY_MS);
            if (rampStart > +prev.date) {
                out.push({ date: new Date(rampStart), value: prev.value });
            }
            out.push(cur);
            continue;
        }
        let cum = 0;
        for (let d = 0; d < gapDays.length; d++) {
            cum += weights[d];
            const boundary = gapDays[d] + ONE_DAY_MS;
            const value = prev.value + (cur.value - prev.value) * (cum / total);
            if (boundary >= +cur.date) {
                out.push(cur);
            } else {
                out.push({ date: new Date(boundary), value });
            }
        }
    }
    return out;
}

// Each sloped segment becomes an S-curve; flat stretches stay flat.
function smoothRamps(points: SeriesPoint[]): SeriesPoint[] {
    if (points.length < 2) return points;

    const out: SeriesPoint[] = [points[0]];
    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        if (a.value === b.value) {
            out.push(b);
            continue;
        }
        const span = +b.date - +a.date;
        for (let j = 1; j <= SMOOTH_STEPS; j++) {
            const f = j / SMOOTH_STEPS;
            out.push({
                date: new Date(+a.date + span * f),
                value: a.value + (b.value - a.value) * smoothstep(f),
            });
        }
    }
    return out;
}

// ---------------------------------------------------------------------------
// Hover state
// ---------------------------------------------------------------------------

interface HoverState {
    x: number;
    tooltipLeft: number;
    tooltipTop: number;
    date: string;
    primaryValue: number | null;
    // the daily bucket the cursor is over — the same one the bars are drawn from
    flow: DailyFlow | null;
    flipLeft: boolean;
}

function utcMidnight(d: Date): number {
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TraderPnlChartProps {
    primaryPoints: PnlPoint[] | BalancePoint[];
    flowPoints: FlowPoint[];
    chartMode: ChartMode;
    flowMode: FlowMode;
    zoomDomain: [Date, Date] | null;
    globalDomain: [Date, Date] | null;
    onZoom: (domain: [Date, Date]) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TraderPnlChart({
    primaryPoints,
    flowPoints,
    chartMode,
    flowMode,
    zoomDomain,
    globalDomain,
    onZoom,
}: TraderPnlChartProps) {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [hover, setHover] = useState<HoverState | null>(null);
    const setHoverRef = useRef(setHover);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(entries => {
            const { width, height } = entries[0].contentRect;
            setSize({ width: Math.floor(width), height: Math.floor(height) });
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const dragStartX = useRef<number | null>(null);
    const { width, height } = size;

    useEffect(() => {
        if (!svgRef.current || width === 0 || height === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const VOL_RATIO = 0.22;
        const GAP = 4;
        const totalInnerH = height - MARGIN.top - MARGIN.bottom;
        const volH = Math.max(20, Math.floor(totalInnerH * VOL_RATIO));
        const pnlH = totalInnerH - volH - GAP;
        const innerW = width - MARGIN.left - MARGIN.right;

        const isDark = theme.palette.mode === 'dark';
        const posColor = theme.palette.success.main;
        const negColor = theme.palette.error.main;
        const portColor = theme.palette.primary.main;
        const gridColor = isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.06)';
        const gridLineColor = isDark
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.07)';
        const textColor = theme.palette.text.secondary;

        const now = new Date();
        const isPnlShape = chartMode === 'pnl' || chartMode === 'portfolio';

        // ── Parse primary series ──
        const primaryParsed: { date: Date; value: number | null }[] = [];
        if (isPnlShape) {
            for (const pt of primaryPoints as PnlPoint[]) {
                const d = new Date(pt.ts);
                if (d > now) continue;
                const v =
                    chartMode === 'pnl' ? pt.total_pnl : pt.portfolio_value;
                primaryParsed.push({ date: d, value: v });
            }
        } else {
            // Cumulative balance at bucket D already includes D's own flows, so the value is
            // only true at the END of D. Emitting (D, previous) and (D + 1 day, value) makes the
            // line ramp across the day the movement belongs to and stay flat between days.
            const sorted = [...(primaryPoints as BalancePoint[])].sort(
                (a, b) => +new Date(a.ts) - +new Date(b.ts)
            );
            let prev = 0;
            for (const pt of sorted) {
                const d = new Date(pt.ts);
                const end = new Date(d.getTime() + 86_400_000);
                if (d <= now) primaryParsed.push({ date: d, value: prev });
                if (end <= now)
                    primaryParsed.push({ date: end, value: pt.balance_usd });
                prev = pt.balance_usd;
            }
        }
        primaryParsed.sort((a, b) => a.date.getTime() - b.date.getTime());

        // ── Aggregate hourly flows → daily buckets for rendering ──
        // Bars are always daily regardless of the raw hourly resolution,
        // so their width matches the primary (daily) series on the x-axis.
        const dailyFlows = aggregateFlowsToDaily(flowPoints);

        // Keyed by UTC midnight so the tooltip reads exactly the bucket a bar was drawn from.
        const dailyMap = new Map<number, DailyFlow>(
            dailyFlows.map(d => [d.date.getTime(), d])
        );

        const { posKey, negKey } = flowBarKeys(flowMode);

        // Only daily buckets with something to show in the selected mode
        const activeDailyBars = dailyFlows.filter(
            d =>
                (d[posKey as keyof DailyFlow] as number) > 0 ||
                (d[negKey as keyof DailyFlow] as number) > 0
        );

        // ── X domain: use primary series extent (flow hourly data can be noisier) ──
        const primaryDates = primaryParsed.map(d => d.date);
        if (primaryDates.length === 0 && dailyFlows.length === 0) return;

        const allExtentDates =
            primaryDates.length > 0
                ? primaryDates
                : dailyFlows.map(d => d.date);
        const fullDomain = d3.extent(allExtentDates) as [Date, Date];
        const xDomain = zoomDomain ?? globalDomain ?? fullDomain;
        const xScale = d3.scaleTime().domain(xDomain).range([0, innerW]);

        // ── Bar width: a bar covers its whole bucket, [day, day + 1 day) ──
        // The aggregate says nothing about when inside the day a transfer happened, so the bar
        // spans the full day rather than marking a point on it.
        const slotPx =
            xScale(new Date(xDomain[0].getTime() + ONE_DAY_MS)) -
            xScale(xDomain[0]);
        const barGap = slotPx > 6 ? 1 : 0;
        const bw = Math.max(1, slotPx - barGap);

        // ── Filter non-null primary points ──
        const validPrimary = primaryParsed.filter(
            (d): d is { date: Date; value: number } => d.value !== null
        );
        if (validPrimary.length < 1 && dailyFlows.length === 0) return;

        // Extend back to the shared domain start, which the flow bars can reach before the
        // first snapshot exists. Without it the left of the chart is empty while bars are
        // already drawn under it.
        let parsed = validPrimary;
        let pnlOffset = 0;
        if (isPnlShape && parsed.length >= 1) {
            const seriesStart = globalDomain?.[0] ?? fullDomain[0];
            if (parsed[0].date > seriesStart) {
                parsed = [{ date: seriesStart, value: 0 }, ...parsed];
            }
            if (chartMode === 'pnl') {
                if (zoomDomain) {
                    const baseline = parsed.filter(
                        d => d.date <= zoomDomain[0]
                    );
                    if (baseline.length > 0)
                        pnlOffset = baseline[baseline.length - 1].value;
                }
                parsed = parsed.map(d => ({
                    ...d,
                    value: d.value - pnlOffset,
                }));
            }
        }

        // ── Y scale (primary) ──
        let yScale: d3.ScaleLinear<number, number>;
        const visible = parsed.filter(
            d => d.date >= xDomain[0] && d.date <= xDomain[1]
        );
        const yData = visible.length >= 2 ? visible : parsed;

        if (yData.length >= 1) {
            const yExt = d3.extent(yData, d => d.value) as [number, number];
            const yPad = Math.max((yExt[1] - yExt[0]) * 0.1, 5);
            yScale =
                chartMode === 'pnl'
                    ? d3
                          .scaleLinear()
                          .domain([Math.min(0, yExt[0] - yPad), yExt[1] + yPad])
                          .range([pnlH, 0])
                    : d3
                          .scaleLinear()
                          .domain([
                              Math.min(0, yExt[0] - yPad),
                              Math.max(yExt[1], 1) * 1.1,
                          ])
                          .range([pnlH, 0]);
        } else {
            yScale = d3.scaleLinear().domain([0, 1]).range([pnlH, 0]);
        }

        // ── Y scale (sub-chart): symmetric, domain = max of pos or neg across ALL daily bars ──
        // Use all dailyFlows (not just activeDailyBars) so the scale is stable when
        // switching flowMode between two modes that have different active sets.
        const maxPos =
            d3.max(dailyFlows, d => d[posKey as keyof DailyFlow] as number) ??
            0;
        const maxNeg =
            d3.max(dailyFlows, d => d[negKey as keyof DailyFlow] as number) ??
            0;
        const maxVol = Math.max(maxPos, maxNeg, 1);
        const volMid = volH / 2;
        const yVolPos = d3
            .scaleLinear()
            .domain([0, maxVol * 1.05])
            .range([volMid, 0]);
        const yVolNeg = d3
            .scaleLinear()
            .domain([0, maxVol * 1.05])
            .range([volMid, volH]);

        // ── Defs ──
        const defs = svg.append('defs');
        defs.append('clipPath')
            .attr('id', 'pnl-clip')
            .append('rect')
            .attr('width', innerW)
            .attr('height', pnlH);
        defs.append('clipPath')
            .attr('id', 'vol-clip')
            .append('rect')
            .attr('x', -(bw + 2))
            .attr('width', innerW + 2 * bw + 4)
            .attr('height', volH + 1);

        // ── Gradient ──
        const gradId = 'trader-pnl-grad';
        const grad = defs
            .append('linearGradient')
            .attr('id', gradId)
            .attr('x1', 0)
            .attr('x2', 0)
            .attr('y1', 0)
            .attr('y2', 1);

        if (chartMode !== 'pnl') {
            grad.append('stop')
                .attr('offset', '0%')
                .attr('stop-color', portColor)
                .attr('stop-opacity', 0.18);
            grad.append('stop')
                .attr('offset', '100%')
                .attr('stop-color', portColor)
                .attr('stop-opacity', 0.02);
        } else {
            const zeroY = Math.max(0, Math.min(pnlH, yScale(0)));
            const zeroFrac = zeroY / pnlH;
            grad.append('stop')
                .attr('offset', '0%')
                .attr('stop-color', posColor)
                .attr('stop-opacity', 0.14);
            grad.append('stop')
                .attr('offset', `${zeroFrac * 100}%`)
                .attr('stop-color', posColor)
                .attr('stop-opacity', 0.02);
            if (zeroFrac < 0.99) {
                grad.append('stop')
                    .attr('offset', `${zeroFrac * 100}%`)
                    .attr('stop-color', negColor)
                    .attr('stop-opacity', 0.02);
                grad.append('stop')
                    .attr('offset', '100%')
                    .attr('stop-color', negColor)
                    .attr('stop-opacity', 0.12);
            }
        }

        const g = svg
            .append('g')
            .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

        // ── Grid ──
        const xTicks = xScale.ticks(5);
        xTicks.forEach(tick => {
            const tx = xScale(tick);
            g.append('line')
                .attr('x1', tx)
                .attr('x2', tx)
                .attr('y1', 0)
                .attr('y2', pnlH + GAP + volH)
                .attr('stroke', gridLineColor)
                .attr('stroke-width', 1);
        });
        yScale.ticks(4).forEach(tick => {
            g.append('line')
                .attr('x1', 0)
                .attr('x2', innerW)
                .attr('y1', yScale(tick))
                .attr('y2', yScale(tick))
                .attr('stroke', gridColor)
                .attr('stroke-width', 1);
        });

        // Zero baseline — drawn whenever the domain crosses zero, so negative portfolio or
        // balance values read as negative instead of looking like missing data.
        if (yScale.domain()[0] < 0) {
            const zeroY = Math.max(0, Math.min(pnlH, yScale(0)));
            g.append('line')
                .attr('x1', 0)
                .attr('x2', innerW)
                .attr('y1', zeroY)
                .attr('y2', zeroY)
                .attr(
                    'stroke',
                    isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'
                )
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '3,3');
        }

        // ── Primary line + area ──
        const lineColor =
            chartMode === 'pnl' &&
            parsed.length > 0 &&
            parsed[parsed.length - 1].value < 0
                ? negColor
                : chartMode === 'pnl'
                  ? posColor
                  : portColor;

        // Smoothing is applied to the drawn geometry only — `parsed` stays the logical
        // series the hover reads, so the tooltip never reports an interpolated value.
        // One geometry for drawing AND hover, so the dot can never leave the line. For
        // snapshots, toDailyRamps maps the row stamped D onto [D-1, D] — the same span the
        // bucket-open flow bars occupy for that day — placing multi-day gaps by activity.
        const activityWeight = (dayStartMs: number): number => {
            const f = dailyMap.get(dayStartMs);
            if (!f) return 0;
            return (
                Math.abs(f.deposit_usd) +
                Math.abs(f.withdraw_usd) +
                Math.abs(f.buy_volume_usd) +
                Math.abs(f.sell_volume_usd) +
                Math.abs(f.inflow_usd) +
                Math.abs(f.outflow_usd)
            );
        };
        let isIntraday = false;
        for (let i = 1; i < parsed.length; i++) {
            if (+parsed[i].date - +parsed[i - 1].date < ONE_DAY_MS) {
                isIntraday = true;
                break;
            }
        }
        const geom =
            isPnlShape && !isIntraday
                ? toDailyRamps(parsed, activityWeight)
                : parsed;
        const drawn = isIntraday ? geom : smoothRamps(geom);
        const curve = isIntraday ? d3.curveStepAfter : d3.curveLinear;

        const pnlG = g.append('g').attr('clip-path', 'url(#pnl-clip)');
        if (parsed.length >= 2) {
            const area = d3
                .area<{ date: Date; value: number }>()
                .x(d => xScale(d.date))
                .y0(pnlH)
                .y1(d => yScale(d.value))
                .curve(curve);
            pnlG.append('path')
                .datum(drawn)
                .attr('fill', `url(#${gradId})`)
                .attr('d', area);

            const line = d3
                .line<{ date: Date; value: number }>()
                .x(d => xScale(d.date))
                .y(d => yScale(d.value))
                .curve(curve);
            pnlG.append('path')
                .datum(drawn)
                .attr('fill', 'none')
                .attr('stroke', lineColor)
                .attr('stroke-width', 1.5)
                .attr('d', line);
        }

        // ── Sub-chart: volume group ──
        const volG = g
            .append('g')
            .attr('transform', `translate(0,${pnlH + GAP})`)
            .attr('clip-path', 'url(#vol-clip)');

        // Mid baseline
        volG.append('line')
            .attr('x1', 0)
            .attr('x2', innerW)
            .attr('y1', volMid)
            .attr('y2', volMid)
            .attr(
                'stroke',
                isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'
            )
            .attr('stroke-width', 1);

        // ── Daily flow bars ──
        activeDailyBars.forEach(day => {
            const cx = xScale(day.date);
            if (cx + bw < 0 || cx > innerW) return;

            const posVal = day[posKey as keyof DailyFlow] as number;
            const negVal = day[negKey as keyof DailyFlow] as number;

            if (posVal > 0) {
                const top = yVolPos(posVal);
                const barH = Math.max(1, volMid - top);
                volG.append('rect')
                    .attr('x', cx)
                    .attr('y', top)
                    .attr('width', bw)
                    .attr('height', barH)
                    .attr('fill', posColor)
                    .attr('opacity', 0.65)
                    .attr('rx', 1);
            }
            if (negVal > 0) {
                const barH = Math.max(1, yVolNeg(negVal) - volMid);
                volG.append('rect')
                    .attr('x', cx)
                    .attr('y', volMid)
                    .attr('width', bw)
                    .attr('height', barH)
                    .attr('fill', negColor)
                    .attr('opacity', 0.65)
                    .attr('rx', 1);
            }
        });

        // ── Axes ──
        g.append('g')
            .attr('transform', `translate(0,${pnlH + GAP + volH})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(xTicks)
                    .tickSize(0)
                    .tickPadding(5)
            )
            .call(ax => ax.select('.domain').remove())
            .call(ax =>
                ax
                    .selectAll('text')
                    .attr('font-size', '10px')
                    .attr('fill', textColor)
                    .attr('font-family', 'inherit')
            );

        g.append('g')
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(4)
                    .tickSize(0)
                    .tickPadding(5)
                    .tickFormat(d => fmtAxis(d as number))
            )
            .call(ax => ax.select('.domain').remove())
            .call(ax =>
                ax
                    .selectAll('text')
                    .attr('font-size', '10px')
                    .attr('fill', textColor)
                    .attr('font-family', 'monospace')
            );

        // ── Drag-to-zoom ──
        const selRect = g
            .append('rect')
            .attr('class', 'zoom-sel')
            .attr('y', 0)
            .attr('height', pnlH + GAP + volH)
            .attr(
                'fill',
                isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,100,255,0.07)'
            )
            .attr(
                'stroke',
                isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,100,255,0.3)'
            )
            .attr('stroke-width', 1)
            .style('display', 'none')
            .style('pointer-events', 'none');

        // ── Hover ──
        const dotColor =
            chartMode === 'pnl' &&
            parsed.length > 0 &&
            parsed[parsed.length - 1].value < 0
                ? negColor
                : chartMode === 'pnl'
                  ? posColor
                  : portColor;

        const hoverG = g.append('g').style('display', 'none');
        hoverG
            .append('rect')
            .attr('class', 'h-band')
            .attr('y', 0)
            .attr('height', pnlH + GAP + volH)
            .attr(
                'fill',
                isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'
            );
        hoverG
            .append('line')
            .attr('class', 'h-edge')
            .attr('y1', 0)
            .attr('y2', pnlH + GAP + volH)
            .attr(
                'stroke',
                isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)'
            )
            .attr('stroke-width', 1);
        hoverG
            .append('circle')
            .attr('class', 'h-dot')
            .attr('r', 4)
            .attr('fill', dotColor)
            .attr('stroke', theme.palette.background.paper)
            .attr('stroke-width', 2);

        const bisectPrimary = d3.bisector<{ date: Date; value: number }, Date>(
            d => d.date
        ).right;
        const bisectSnap = d3.bisector<{ date: Date; value: number }, Date>(
            d => d.date
        ).left;

        const overlay = svg
            .append('rect')
            .attr('fill', 'none')
            .attr('pointer-events', 'all')
            .attr('cursor', 'crosshair')
            .attr('width', innerW)
            .attr('height', pnlH + GAP + volH)
            .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

        overlay
            .on('mousemove', function (event) {
                if (dragStartX.current !== null) return;
                const [mx] = d3.pointer(event, this);
                const rawDate = xScale.invert(mx);

                // One hovered unit = one UTC day, the resolution the bars and the aggregates
                // actually have. Everything below is resolved for that day, so the label, the
                // dot and the bars can never describe different days.
                let dayStart = new Date(utcMidnight(rawDate));
                let cx = 0;
                let primaryValue: number | null = null;
                let dotY = pnlH / 2;
                let dateLabel = '';

                if (isIntraday && parsed.length > 0) {
                    const dayEnd = new Date(dayStart.getTime() + ONE_DAY_MS);
                    const fi = bisectSnap(parsed, dayStart, 0);
                    const dayHasPoint =
                        fi < parsed.length && +parsed[fi].date < +dayEnd;
                    if (dayHasPoint) {
                        const si = bisectSnap(parsed, rawDate, 1);
                        const p0 = parsed[si - 1] ?? parsed[0];
                        const p1 = parsed[si];
                        const snap =
                            !p1 ||
                            Math.abs(+rawDate - +p0.date) <=
                                Math.abs(+rawDate - +p1.date)
                                ? p0
                                : p1;
                        dayStart = new Date(utcMidnight(snap.date));
                        cx = xScale(snap.date);
                        primaryValue = snap.value;
                        dotY = yScale(primaryValue);
                        dateLabel = snap.date.toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'UTC',
                        });
                    } else {
                        const pi = bisectPrimary(parsed, dayEnd, 0) - 1;
                        if (pi >= 0) {
                            primaryValue = parsed[pi].value;
                            dotY = yScale(primaryValue);
                        }
                        const last = parsed[parsed.length - 1].date;
                        cx = xScale(+dayEnd > +last ? last : dayEnd);
                        dateLabel = dayStart.toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            timeZone: 'UTC',
                        });
                    }
                } else {
                    const dayEnd = new Date(dayStart.getTime() + ONE_DAY_MS);
                    // Value at the end of the highlighted day, read off the same geometry the
                    // line is drawn from. All geometry points are day-aligned, so the last point
                    // at or before dayEnd is exact.
                    if (geom.length >= 1) {
                        const pi = bisectPrimary(geom, dayEnd, 0) - 1;
                        if (pi >= 0)
                            primaryValue =
                                geom[Math.min(pi, geom.length - 1)].value;
                        if (primaryValue !== null) dotY = yScale(primaryValue);
                    }
                    // The day's value is reached at its end, which is where the peak sits.
                    cx = xScale(dayEnd);
                    dateLabel = dayStart.toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        timeZone: 'UTC',
                    });
                }

                const nearestFlow = dailyMap.get(dayStart.getTime()) ?? null;

                const bandX = xScale(dayStart);
                const bandW = Math.max(
                    1,
                    xScale(new Date(dayStart.getTime() + ONE_DAY_MS)) - bandX
                );

                hoverG.style('display', null);
                hoverG.select('.h-band').attr('x', bandX).attr('width', bandW);
                hoverG.select('.h-edge').attr('x1', cx).attr('x2', cx);
                hoverG
                    .select('.h-dot')
                    .style('display', primaryValue === null ? 'none' : '')
                    .attr('cx', cx)
                    .attr('cy', dotY);

                const absX = cx + MARGIN.left;
                const dotAbsY = dotY + MARGIN.top;
                // Estimated tooltip height: with flow rows ~145px, without ~45px.
                // Try anchoring the tooltip below the dot; if that clips the bottom, anchor it
                // above instead; then clamp into the container so it can never be cut off.
                const tooltipEstH = nearestFlow ? 145 : 45;
                let tooltipTop = dotAbsY - 14;
                if (tooltipTop + tooltipEstH > height - 4) {
                    tooltipTop = dotAbsY - 4 - tooltipEstH;
                }
                tooltipTop = Math.max(
                    4,
                    Math.min(tooltipTop, height - tooltipEstH - 4)
                );
                const flipLeft = absX + TOOLTIP_W + 16 > width;
                const tooltipLeft = Math.max(
                    4,
                    Math.min(
                        flipLeft ? absX - TOOLTIP_W - 6 : absX + 10,
                        width - TOOLTIP_W - 4
                    )
                );
                setHoverRef.current({
                    x: absX,
                    tooltipLeft,
                    tooltipTop,
                    date: dateLabel,
                    primaryValue:
                        primaryValue !== null ? primaryValue + pnlOffset : null,
                    flow: nearestFlow,
                    flipLeft,
                });
            })
            .on('mouseleave', () => {
                if (dragStartX.current !== null) return;
                hoverG.style('display', 'none');
                setHoverRef.current(null);
            })
            .on('mousedown', function (event) {
                event.preventDefault();
                hoverG.style('display', 'none');
                setHoverRef.current(null);
                const [mx] = d3.pointer(event, this);
                dragStartX.current = mx;
            });

        const onMouseMove = (event: MouseEvent) => {
            if (dragStartX.current === null || !svgRef.current) return;
            const svgRect = svgRef.current.getBoundingClientRect();
            const mx = Math.max(
                0,
                Math.min(innerW, event.clientX - svgRect.left - MARGIN.left)
            );
            const x0 = Math.min(dragStartX.current, mx);
            const x1 = Math.max(dragStartX.current, mx);
            selRect
                .style('display', x1 - x0 > 4 ? 'block' : 'none')
                .attr('x', x0)
                .attr('width', x1 - x0);
        };

        const onMouseUp = (event: MouseEvent) => {
            if (dragStartX.current === null || !svgRef.current) return;
            const svgRect = svgRef.current.getBoundingClientRect();
            const mx = Math.max(
                0,
                Math.min(innerW, event.clientX - svgRect.left - MARGIN.left)
            );
            const x0 = Math.min(dragStartX.current, mx);
            const x1 = Math.max(dragStartX.current, mx);
            dragStartX.current = null;
            selRect.style('display', 'none');
            if (x1 - x0 < 8) return;
            onZoom([xScale.invert(x0), xScale.invert(x1)]);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [
        primaryPoints,
        flowPoints,
        chartMode,
        flowMode,
        width,
        height,
        theme,
        zoomDomain,
        globalDomain,
        onZoom,
    ]);

    const primaryLabel =
        chartMode === 'pnl'
            ? 'PnL'
            : chartMode === 'portfolio'
              ? 'Portfolio'
              : 'Balance';

    const primaryValueColor = (v: number | null) => {
        if (v === null) return 'text.disabled';
        if (chartMode === 'pnl') return v >= 0 ? 'success.main' : 'error.main';
        return 'primary.main';
    };

    const fmtPrimary = (v: number) =>
        chartMode === 'pnl' ? fmtPnlSigned(v) : fmtDollar(v);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 160,
                position: 'relative',
            }}
        >
            <svg
                ref={svgRef}
                width={width}
                height={height}
                style={{ display: 'block' }}
            />
            {hover && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: hover.tooltipLeft,
                        top: hover.tooltipTop,
                        pointerEvents: 'none',
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '4px',
                        px: 1,
                        py: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25,
                        zIndex: 10,
                        width: TOOLTIP_W,
                    }}
                >
                    <Typography
                        sx={{ fontSize: '10px', color: 'text.secondary' }}
                    >
                        {hover.date}
                    </Typography>
                    <TooltipRow
                        label={primaryLabel}
                        value={hover.primaryValue}
                        formatter={fmtPrimary}
                        color={primaryValueColor(hover.primaryValue)}
                    />
                    {hover.flow && (
                        <>
                            <Box
                                sx={{
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    mt: 0.25,
                                    mb: 0.25,
                                }}
                            />
                            <TooltipRow
                                label="Deposit"
                                value={hover.flow.deposit_usd}
                                formatter={fmtDollar}
                                color={
                                    hover.flow.deposit_usd > 0
                                        ? 'success.main'
                                        : 'text.disabled'
                                }
                            />
                            <TooltipRow
                                label="Withdraw"
                                value={hover.flow.withdraw_usd}
                                formatter={fmtDollar}
                                color={
                                    hover.flow.withdraw_usd > 0
                                        ? 'error.main'
                                        : 'text.disabled'
                                }
                            />
                            <TooltipRow
                                label="Buy vol"
                                value={hover.flow.buy_volume_usd}
                                formatter={fmtDollar}
                                color="text.secondary"
                            />
                            <TooltipRow
                                label="Sell vol"
                                value={hover.flow.sell_volume_usd}
                                formatter={fmtDollar}
                                color="text.secondary"
                            />
                            <TooltipRow
                                label="Inflow"
                                value={hover.flow.inflow_usd}
                                formatter={fmtDollar}
                                color="text.secondary"
                            />
                            <TooltipRow
                                label="Outflow"
                                value={hover.flow.outflow_usd}
                                formatter={fmtDollar}
                                color="text.secondary"
                            />
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Tooltip row
// ---------------------------------------------------------------------------

function TooltipRow({
    label,
    value,
    formatter,
    color,
}: {
    label: string;
    value: number | null;
    formatter: (v: number) => string;
    color: string;
}) {
    return (
        <Box
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}
        >
            <Typography
                sx={{ fontSize: '10px', color: 'text.disabled', flexShrink: 0 }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color,
                }}
            >
                {value === null || value === 0 ? '—' : formatter(value)}
            </Typography>
        </Box>
    );
}
